import { useMemo, useState } from "react";
import {
  formatAsCrore,
  calculateExpectedCorpus,
  calculateMonthlySIP,
  calculateFutureValue,
} from "creso-core/utils";
import { CalculatorInput } from "./calculator-input";
import { CalculatorChart } from "./calculator-chart";
import { Disclaimer } from "./disclaimer";
import { CalculatorSnapshotContainer } from "./snapshot-container";
import { CalculatorResultContainer } from "./result-container";
import { CalculatorResultSummary } from "./result-summary";
import { CalculatorInputSummary } from "./input-summary";
import { SnapshotButton } from "./snapshot-btn";

const DEFAULTS = {
  targetAmount: 1_00_000_000,
  duration: 10,
  expectedReturn: 12,
  inflation: 6,
};

export function GoalSIPCalculator({
  brandRender,
}: {
  brandRender: () => React.ReactNode | null;
}) {
  const [targetAmount, setTargetAmount] = useState(DEFAULTS.targetAmount);
  const [duration, setDuration] = useState(DEFAULTS.duration);
  const [expectedReturn, setExpectedReturn] = useState(DEFAULTS.expectedReturn);
  const [inflation, setInflation] = useState(DEFAULTS.inflation);

  const { monthlySIP, totalInvestment, inflatedTarget, chartData } =
    useMemo(() => {
      const inflated = calculateFutureValue(
        targetAmount,
        inflation,
        duration * 12
      );
      const sip = calculateMonthlySIP(inflated, expectedReturn, duration * 12);
      const totalInvested = sip * duration * 12;

      const points = Array.from({ length: duration }, (_, i) => {
        const year = i + 1;
        const corpus = calculateExpectedCorpus(
          sip,
          0,
          expectedReturn,
          year * 12
        );
        const invested = sip * year * 12;
        return {
          label: `Y${year}`,
          invested,
          gains: Math.max(0, corpus - invested),
        };
      });

      return {
        monthlySIP: sip,
        totalInvestment: totalInvested,
        inflatedTarget: inflated,
        chartData: points,
      };
    }, [targetAmount, duration, expectedReturn, inflation]);

  const summaryItems = [
    { label: "Monthly SIP", value: formatAsCrore(monthlySIP) },
    { label: "Total Invested", value: formatAsCrore(totalInvestment) },
    {
      label: "Inflation Adj. Target",
      value: formatAsCrore(inflatedTarget),
    },
  ];

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-6">
        <CalculatorInput
          label="Target Amount"
          value={targetAmount}
          onChange={setTargetAmount}
          min={1_00_000}
          max={100_00_00_000}
          step={1_00_000}
          unit="₹"
          formatDisplay={(v) => formatAsCrore(v, 1, true)}
        />
        <CalculatorInput
          label="Duration"
          value={duration}
          onChange={setDuration}
          min={1}
          max={40}
          step={1}
          unit="yr"
        />
        <CalculatorInput
          label="Expected Returns"
          value={expectedReturn}
          onChange={setExpectedReturn}
          min={1}
          max={50}
          step={0.5}
          unit="%"
        />
        <CalculatorInput
          label="Inflation Rate"
          value={inflation}
          onChange={setInflation}
          min={0}
          max={20}
          step={0.5}
          unit="%"
        />
      </div>

      <CalculatorSnapshotContainer
        brandRender={brandRender}
        inputRender={() => (
          <CalculatorInputSummary
            header="Required SIP Calculation"
            fields={[
              { label: "Target Amount", value: formatAsCrore(targetAmount) },
              { label: "Duration", value: `${duration} year(s)` },
              { label: "Expected Returns", value: `${expectedReturn}%` },
              { label: "Inflation Rate", value: `${inflation}%` },
            ]}
          />
        )}
        resultRender={({ onSnapshot }) => (
          <CalculatorResultContainer>
            <CalculatorResultSummary items={summaryItems} />
            <div className="min-h-85 relative w-full pt-12 mb-2">
              <SnapshotButton onSnapshot={onSnapshot} />
              <CalculatorChart data={chartData} />
            </div>
            <Disclaimer />
          </CalculatorResultContainer>
        )}
      />
    </div>
  );
}
